import { Button } from "../components/Button";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";
const ButtonsSwitchContext = React.createContext(null);
const useButtonsSwitch = () => {
    const ctx = React.useContext(ButtonsSwitchContext);
    if (!ctx) {
        throw new Error("ButtonsSwitch must be used within a ButtonsSwitchList component");
    }
    return ctx;
};
const listStyles = cva(cn("s-inline-flex s-items-center s-gap-1", "s-box-border s-bg-muted s-border s-border-border dark:s-border-border-night dark:s-bg-muted-night"), {
    variants: {
        fullWidth: {
            true: "s-w-full",
            false: "",
        },
        size: {
            xs: "s-rounded-xl s-p-[3px]",
            sm: "s-rounded-2xl s-p-1",
            md: "s-rounded-3xl s-p-1.5",
        },
    },
    defaultVariants: {
        fullWidth: false,
        size: "sm",
    },
});
export const ButtonsSwitchList = React.forwardRef(({ className, children, size = "sm", defaultValue, onValueChange, disabled, fullWidth, ...props }, ref) => {
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const selected = internalValue;
    const handleChange = React.useCallback((next) => {
        setInternalValue(next);
        onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(next);
    }, [onValueChange]);
    const context = React.useMemo(() => ({ value: selected, onValueChange: handleChange, size, disabled }), [selected, handleChange, size, disabled]);
    return (React.createElement("div", { ref: ref, role: "tablist", "aria-orientation": "horizontal", className: cn(listStyles({ fullWidth, size }), className), ...props },
        React.createElement(ButtonsSwitchContext.Provider, { value: context }, children)));
});
ButtonsSwitchList.displayName = "ButtonsSwitchList";
export const ButtonsSwitch = React.forwardRef(({ className, value, label, icon, disabled, onClick, ...props }, ref) => {
    const { value: selected, onValueChange, size, disabled: groupDisabled, } = useButtonsSwitch();
    const isActive = selected === value;
    const isDisabled = disabled || groupDisabled;
    const handleClick = (e) => {
        if (isDisabled) {
            return;
        }
        onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(value);
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    };
    return (React.createElement(Button, { ref: ref, role: "tab", "aria-selected": isActive, size: size, variant: isActive ? "outline" : "ghost", label: label, icon: icon, className: className, disabled: isDisabled, onClick: handleClick, ...props }));
});
ButtonsSwitch.displayName = "ButtonsSwitch";
//# sourceMappingURL=ButtonsSwitch.js.map